import { Skeleton } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { useTranslation } from '@/core/hooks';
import type { ReservationStatus } from '@/core/reservation';
import React from 'react';
import { StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';
import { getReservationVisibleCopy } from '../constants/webReservationStatus';
import { ReservationStatusToneIcon } from './ReservationStatusToneIcon';

type TypographyColor = React.ComponentProps<typeof Typography>['color'];
type TypographyVariant = React.ComponentProps<typeof Typography>['variant'];

const DEFAULT_ICON_COLOR = '#666666';

export interface ReservationStatusMessageProps {
  readonly mappedStatus: ReservationStatus | null;
  readonly rawStatus?: string | null;
  readonly color?: TypographyColor;
  /** Hex/token used for the tone icon when `showToneIcon` is true. */
  readonly iconColor?: string;
  readonly variant?: TypographyVariant;
  readonly style?: StyleProp<TextStyle>;
  readonly rowStyle?: StyleProp<ViewStyle>;
  readonly skeletonWidth?: number | `${number}%`;
  readonly skeletonHeight?: number;
  /** When true, icon and message appear together after Contentful settles. */
  readonly showToneIcon?: boolean;
  readonly iconSize?: number;
}

function resolveToneIconColor(
  iconColor: string | undefined,
  color: TypographyColor | undefined,
): string {
  if (iconColor) {
    return iconColor;
  }
  if (typeof color === 'string') {
    return color;
  }
  return DEFAULT_ICON_COLOR;
}

function MessageLoadingSkeleton({
  showToneIcon,
  iconSize,
  skeletonWidth,
  skeletonHeight,
  rowStyle,
}: Readonly<{
  showToneIcon: boolean;
  iconSize: number;
  skeletonWidth: number | `${number}%`;
  skeletonHeight: number;
  rowStyle?: StyleProp<ViewStyle> | undefined;
}>) {
  const textSkeleton = (
    <Skeleton
      width={skeletonWidth}
      height={skeletonHeight}
      borderRadius={4}
    />
  );

  if (!showToneIcon) {
    return textSkeleton;
  }

  return (
    <View style={[styles.row, rowStyle]}>
      <Skeleton
        width={iconSize}
        height={iconSize}
        variant="circular"
        borderRadius={iconSize / 2}
      />
      {textSkeleton}
    </View>
  );
}

/**
 * Renders reservation-status copy (and optional tone icon):
 * shimmer until Contentful settles, then the Contentful value or the raw
 * `current_status` on a miss. An empty status shows nothing.
 * Icon is gated on the same ready flag so it never appears before the message.
 */
export function ReservationStatusMessage({
  mappedStatus,
  rawStatus = null,
  color = 'secondary',
  iconColor,
  variant = 'body',
  style,
  rowStyle,
  skeletonWidth = 140,
  skeletonHeight = 14,
  showToneIcon = false,
  iconSize = 16,
}: Readonly<ReservationStatusMessageProps>) {
  const { t } = useTranslation();
  const copy = getReservationVisibleCopy(mappedStatus, t, rawStatus);

  if (!mappedStatus || !copy) {
    return null;
  }

  if (!copy.isMessageReady) {
    return (
      <MessageLoadingSkeleton
        showToneIcon={showToneIcon}
        iconSize={iconSize}
        skeletonWidth={skeletonWidth}
        skeletonHeight={skeletonHeight}
        rowStyle={rowStyle}
      />
    );
  }

  if (!copy.listingMessage) {
    return null;
  }

  const message = (
    <Typography variant={variant} color={color} style={style}>
      {copy.listingMessage}
    </Typography>
  );

  if (!showToneIcon) {
    return message;
  }

  return (
    <View style={[styles.row, rowStyle]}>
      <ReservationStatusToneIcon
        mappedStatus={mappedStatus}
        color={resolveToneIconColor(iconColor, color)}
        size={iconSize}
      />
      {message}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
});

export default ReservationStatusMessage;
